import { useCallback, useEffect, useRef, useState } from 'react';

import { ActionIcon, Alert, Group, Loader, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';

import { MarkdownParser } from '@/components';
import { TemplatePreviewResponse } from '@/types';
import classes from './TemplateEditor.module.css';

interface EditorPreviewPaneProps {
  loading: boolean;
  result: TemplatePreviewResponse | null;
  onClose: () => void;
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 1100;
const DEFAULT_WIDTH = 480;

export function EditorPreviewPane({ loading, result, onClose }: Readonly<EditorPreviewPaneProps>) {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [dragging, setDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      dragStateRef.current = { startX: e.clientX, startWidth: width };
      setDragging(true);
    },
    [width]
  );

  useEffect(() => {
    if (!dragging) {
      return;
    }
    // Dragging the handle leftward should grow the panel (more rightward
    // pixels). So width = startWidth - (clientX - startX).
    const onMove = (e: MouseEvent) => {
      const drag = dragStateRef.current;
      if (!drag) {
        return;
      }
      const next = drag.startWidth - (e.clientX - drag.startX);
      const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, next));
      setWidth(clamped);
    };
    const onUp = () => {
      dragStateRef.current = null;
      setDragging(false);
    };
    globalThis.addEventListener('mousemove', onMove);
    globalThis.addEventListener('mouseup', onUp);
    // Block text selection / cursor flicker while dragging.
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      globalThis.removeEventListener('mousemove', onMove);
      globalThis.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
    };
  }, [dragging]);

  const bodyContent = renderBody(loading, result);

  return (
    <div className={classes.previewPane} style={{ width }}>
      <button
        type="button"
        className={classes.previewResizeHandle}
        onMouseDown={handleMouseDown}
        onDoubleClick={() => setWidth(DEFAULT_WIDTH)}
        aria-label="Resize preview (double-click to reset)"
      />

      <Group justify="space-between" className={classes.previewHeader}>
        <Text fw={600} size="sm">
          Preview
        </Text>
        <ActionIcon variant="subtle" onClick={onClose} aria-label="Close preview">
          <IconX size={16} />
        </ActionIcon>
      </Group>

      <div className={classes.previewBody}>{bodyContent}</div>
    </div>
  );
}

function renderBody(loading: boolean, result: TemplatePreviewResponse | null) {
  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (result === null) {
    return (
      <Text c="dimmed" size="sm">
        Click Preview to render the template against sample data.
      </Text>
    );
  }
  if (result.errors.length > 0) {
    return (
      <Stack gap="sm">
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertTriangle size={16} />}
          title="Render failed"
        >
          <Stack gap={4}>
            {result.errors.map((e) => (
              <Text key={`${e.line ?? 'na'}-${e.message}`} size="xs">
                {e.line != null ? `Line ${e.line}: ` : ''}
                {e.message}
              </Text>
            ))}
          </Stack>
        </Alert>
        {result.rendered && <MarkdownParser content={result.rendered} />}
      </Stack>
    );
  }
  if (result.rendered) {
    return <MarkdownParser content={result.rendered} />;
  }
  return (
    <Text c="dimmed" size="sm">
      (Empty render output)
    </Text>
  );
}
